import React, { useState } from "react";
import { Modal, Form, Select, Input, message } from "antd";
import { useTranslation } from "react-i18next";
import { FeedbackService } from "../services/feedbackService";

const { Option } = Select;

const CATEGORIES = ["problem", "suggestion", "question", "other"];
const PRIORITIES = ["low", "medium", "high"];

// In-app feedback window: category + priority + message, emailed to the support
// inbox by the backend. Canonical keys are sent; labels are localized here.
const FeedbackModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // validation errors are shown inline
    }
    setSubmitting(true);
    try {
      await FeedbackService.sendFeedback({
        category: values.category,
        priority: values.priority,
        message: values.message.trim(),
      });
      message.success(t("feedback.success"));
      form.resetFields();
      onClose();
    } catch (e) {
      message.error(t("feedback.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t("feedback.title")}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={t("feedback.send")}
      cancelText={t("cancel")}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ category: "problem", priority: "medium" }}
        data-testid="feedback.form"
      >
        <Form.Item
          name="category"
          label={t("feedback.category")}
          rules={[{ required: true }]}
        >
          <Select data-testid="feedback.category">
            {CATEGORIES.map((c) => (
              <Option key={c} value={c}>
                {t(`feedback.categories.${c}`)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="priority"
          label={t("feedback.priority")}
          rules={[{ required: true }]}
        >
          <Select data-testid="feedback.priority">
            {PRIORITIES.map((p) => (
              <Option key={p} value={p}>
                {t(`feedback.priorities.${p}`)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="message"
          label={t("feedback.message")}
          rules={[
            { required: true, message: t("feedback.messageRequired") },
            {
              validator: (_, v) =>
                v && v.trim().length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(t("feedback.messageRequired"))),
            },
          ]}
        >
          <Input.TextArea
            rows={5}
            maxLength={1000}
            showCount
            placeholder={t("feedback.messagePlaceholder")}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FeedbackModal;
